import { PropsWithChildren, useCallback, useMemo, useState } from 'react';

import { format, isValid } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { SwingDeleteConfirmationModal } from '@blocks';
import { calculateStatus, convertUnit, isSpeedDisplayUnit, Nil, simpleConvert, UserPreferredUnit } from '@core';
import { GoalReport } from '@data';
import { useDeleteSwing, useLayoutGoalReport } from '@queries';
import { activityUnitAtom } from '@store';
import { Box, Heading3, Spinner, Stack, StatusBox, Text, TrashIcon, u } from '@ui';

import { useGoalReportContext } from '../../GoalReportContext';
import { convertFiltersToGoalReportParams } from '../GoalReportCharts/goalReportCharts.utils';

import * as css from './GoalReportSwings.css';

type GoalReportMap = Record<string, Pick<GoalReport, 'innerMax' | 'innerMin' | 'unit'>>;

export const GoalReportSwings = () => {
    const userPreferredUnit = useAtomValue(activityUnitAtom);
    const { layoutId, filters, customRange, resetFilters } = useGoalReportContext();
    const { data: totalGoalReport } = useLayoutGoalReport({ layoutId });
    const convertedFilters = convertFiltersToGoalReportParams({ ...filters, ...customRange, layoutId });
    const { data: goalReport, isLoading: isLoadingReport } = useLayoutGoalReport(convertedFilters);
    const firstSwingGroup = goalReport?.[0].swings;

    const isEmpty = firstSwingGroup?.length === 0;
    const isReportEmpty = totalGoalReport?.[0].swings?.length === 0;
    const goals = useMemo(() => goalReport?.map((goal) => goal.parameterName) ?? [], [goalReport]);
    const goalReportMap = useMemo(
        () =>
            goals.reduce((acc, goal) => {
                const report = goalReport?.find((gr) => gr.parameterName === goal);
                if (report) {
                    acc[goal] = {
                        innerMin: report.innerMax,
                        innerMax: report.innerMin,
                        unit: report.unit,
                    };
                }
                return acc;
            }, {} as GoalReportMap),
        [goals],
    );

    const valuesByGoalAndSwing = useMemo(() => {
        if (!goalReport) return {};
        return Object.fromEntries(
            goalReport.map((goal) => [
                goal.parameterName,
                Object.fromEntries((goal.swings ?? []).map((swing) => [swing.swingUuid, swing.parameterValue])),
            ]),
        );
    }, [goalReport]);

    const swingRows: SwingRow[] = useMemo(
        () =>
            firstSwingGroup?.map((swing) => {
                const goalMap: Record<string, number | null> = Object.fromEntries(
                    goals.map((goal) => [goal, valuesByGoalAndSwing?.[goal]?.[swing.swingUuid] ?? null]),
                );

                return {
                    activityId: swing.activityId,
                    uuid: swing.swingUuid,
                    createdAt: swing.createdAt,
                    goalMap,
                };
            }) ?? [],
        [firstSwingGroup],
    );

    const tableHeader: Record<string, string> = useMemo(
        () =>
            Object.fromEntries(
                goals.map((key) => {
                    return [key, key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())];
                }),
            ),
        [goals],
    );
    if (isLoadingReport) {
        return <Spinner size="l" />;
    }
    if (!firstSwingGroup || isEmpty) {
        if (isReportEmpty) {
            <Box>
                <Text variant="p">No swings are connected to this goal</Text>
            </Box>;
        }

        return (
            <StatusBox
                status="info"
                title="No swings match your current filters"
                description="Adjust the date range or reset filters to include swings"
                action={{ label: 'Reset filters', handleAction: resetFilters }}
            />
        );
    }
    return (
        <div
            className={css.table}
            style={{ gridTemplateColumns: `${u(5)}px repeat(${goals.length + 2}, max-content)` }}
        >
            <div className={css.row}>
                <div className={css.headerFixed} />
                <div className={css.headerFixedSwing}>Swing</div>
                {goals.map((key) => (
                    <Text className={css.headerCell} key={key}>
                        {tableHeader?.[key]}
                    </Text>
                ))}
                <Text className={css.headerCell}>Remove</Text>
            </div>

            <div className={css.row}>
                {swingRows.map((swingRow, row) => (
                    <SwingTableRow
                        number={row + 1}
                        key={swingRow.uuid}
                        order={goals}
                        userPreferredUnit={userPreferredUnit}
                        goalReportMap={goalReportMap}
                        {...swingRow}
                    />
                ))}
            </div>
        </div>
    );
};

type SwingRow = {
    activityId: number;
    uuid: string;
    createdAt: string;

    goalMap: Record<string, number | Nil>;
};

type SwingRowProps = SwingRow & {
    number: number;
    order: string[];
    userPreferredUnit: UserPreferredUnit;
    goalReportMap: GoalReportMap;
};

function SwingTableRow({
    number,
    uuid,
    createdAt,
    activityId,
    goalMap,
    order,

    userPreferredUnit,
    goalReportMap,
}: SwingRowProps) {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { getFullActivityRoute } = useGoalReportContext();
    const { mutateAsync: deleteSwing, isPending: isDeletingSwing } = useDeleteSwing();

    const handleRemoveSwing = useCallback(async () => {
        await deleteSwing({ swingUuid: uuid, activityId });
        setOpen(false);
        toast('Deleted swing');
    }, [activityId, deleteSwing, uuid]);
    return (
        <>
            <div className={css.row}>
                <div className={css.swingNumberCell}>
                    <Heading3>{number}</Heading3>
                </div>

                <div className={css.swingColumn} onClick={() => navigate(getFullActivityRoute(activityId, uuid))}>
                    <Stack gap={u(0.5)} py={u(0.5)}>
                        <Text variant="p" textAlign={'left'} color="brand">
                            Swing made {formatISODate(createdAt)}
                        </Text>
                    </Stack>
                </div>
                {order.map((key) => (
                    <SwingCell
                        key={key}
                        value={goalMap[key]}
                        userPreferredUnit={userPreferredUnit}
                        goalCorridor={goalReportMap[key]}
                    />
                ))}
                <div className={css.cell({ isLast: true })}>
                    <GhostButton onClick={() => setOpen(true)}>DELETE SWING</GhostButton>
                </div>
            </div>
            {open && (
                <SwingDeleteConfirmationModal
                    isDeleting={isDeletingSwing}
                    onAccept={handleRemoveSwing}
                    onDismiss={() => setOpen(false)}
                />
            )}
        </>
    );
}

type SwingCellProps = {
    value: number | Nil;
    userPreferredUnit: UserPreferredUnit;
    goalCorridor: Pick<GoalReport, 'innerMax' | 'innerMin' | 'unit'> | Nil;
};

const SwingCell = ({ value, userPreferredUnit, goalCorridor }: SwingCellProps) => {
    if (!goalCorridor || !goalCorridor?.unit) {
        return <div />;
    }
    const converted = value
        ? convertUnit({ value, unit: goalCorridor.unit, userPreferredUnit })
        : { value, symbol: '', unit: '' };
    const innerMin = simpleConvert({ value: goalCorridor.innerMax, userPreferredUnit, unit: goalCorridor.unit });
    const innerMax = simpleConvert({ value: goalCorridor.innerMin, userPreferredUnit, unit: goalCorridor.unit });
    const goalStatus = goalCorridor
        ? calculateStatus({
              value: converted.value,
              corridorParameterMargins: { innerMax, innerMin },
          })
        : null;

    const indicator = goalStatus ?? undefined;

    return (
        <div
            className={css.cell({
                indicator,
                isLast: false,
            })}
        >
            <span style={{ lineHeight: '21px' }}>{converted.value ?? '\u2014'}</span>

            {isSpeedDisplayUnit(converted?.unit) || converted.symbol === 'cm' ? (
                <Box style={{ fontSize: '0.667em' }} display="flex">
                    {converted.symbol}
                </Box>
            ) : (
                converted.symbol
            )}
        </div>
    );
};

export function formatISODate(value?: string | Date | null) {
    const date = value instanceof Date ? value : new Date(value ?? '');
    return isValid(date) ? format(date, 'yyyy-MM-dd') : '—';
}

const GhostButton = ({ children, onClick }: PropsWithChildren<{ onClick: () => void }>) => {
    return (
        <button onClick={onClick} className={css.ghostButton}>
            <TrashIcon />
            {children}
        </button>
    );
};
